import { Injectable } from '@angular/core';
import { ArbeitsbereichService } from '../../../shared/service/arbeitsbereich.service';
import { MessageService } from '../../../core/service/message.service';
import { from, Observable } from 'rxjs';
import { MainMapService } from '../../../shared/service/main-map.service';
import { map } from 'rxjs/operators';
import { DetailViewFormBuilderService } from '../../../shared/service/detail-view-form-builder.service';
import { DetailFormDataModel } from '../../../config/model/detail-form-data.model';
import { DetailViewNotifyService } from './detailview-notify.service';
import { SketchGeometryService } from '../../../shared/service/sketch-geometry.service';
import { DetailViewFeatureParams } from '../detail-view.model';
import { LayerContainerService } from '../../../shared/service/layer-container.service';
import { CONSTANTS_VALUE } from '../../../config/constants.module';
import Enums from '../../../shared/enum/enums';
import { UrlStateHelperService } from '../../../shared/service/url-state/url-state-helper.service';
import { GlobalState } from '../../../config/model/global-state.model';
import { SelectionService } from '../../../shared/service/selection.service';

@Injectable({
  providedIn: 'root',
})
export class DetailViewCrudService {
  private _sketchViewModel: __esri.SketchViewModel;
  private _editGraphicsLayer: __esri.GraphicsLayer;
  private _editingGraphic: __esri.Graphic;

  constructor(
    private _arbeitsbereichService: ArbeitsbereichService,
    private _messageService: MessageService,
    private _mainMapService: MainMapService,
    private _detailViewFormBuilderService: DetailViewFormBuilderService,
    private _detailViewNotifyService: DetailViewNotifyService,
    private _sketchGeometryService: SketchGeometryService,
    private _layerContainerService: LayerContainerService,
    private selectionService: SelectionService,
    private readonly urlStateHelperService: UrlStateHelperService
  ) {}

  /**
   * @name deactivateEditTool
   * @description Deactivates the "edit"-tool and stops the editing geomtry
   */
  public deactivateEditTool() {
    this._mainMapService.activateMapInteraction();
    if (this._sketchViewModel) this._sketchViewModel.complete();
    if (this._editGraphicsLayer) {
      this._editGraphicsLayer.graphics.removeAll();
      this._mainMapService.mapView.map.layers.remove(this._editGraphicsLayer);
    }
  }

  /** public Methode **/

  /**
   * @function save
   * @description Save Detail View Form to FeatureLayer
   */
  public saveFeature(
    detailFormDataModel: DetailFormDataModel,
    featureParams: DetailViewFeatureParams,
    featureGeometryEdited: boolean
  ): Observable<any> {
    this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.wait);
    // get editable featurelayer from arbeitsbereich service
    const editableFeatureLyr: __esri.FeatureLayer = this._arbeitsbereichService.arbeitsbereichEditableLyr;
    // set attributes in the correct field type
    this._detailViewFormBuilderService.mapAttributesInCorrectFieldType(
      detailFormDataModel.selectedFeatureFields,
      detailFormDataModel.selectedFeature.attributes
    );

    this.updateFeatureGeometry(detailFormDataModel, featureParams);

    // prepare edits
    const featureToBeSaved = this.getWriteActionForFeature(detailFormDataModel, featureParams);

    return this.save(editableFeatureLyr, featureToBeSaved).pipe(
      map(response => {
        this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
        if (!response.success) return;
        this._arbeitsbereichService.arbeitsbereichMapLyr.refresh();
        this.refreshMap();
        this.selectEditedFeature(featureGeometryEdited, featureToBeSaved);
        if (featureParams.createMode)
          this.completeCreateFeature(detailFormDataModel, featureParams, response.result.addFeatureResults[0]);
        // refresh list-view datatable after feature update
        this._detailViewNotifyService.onFeatureSavedSubject.next();
        return response;
      })
    );
  }

  /**
   * @function deleteFeature
   * @description Entfernt das selektierte Feature physisch aus der FeatureLayer
   */
  public deleteFeature(): Observable<any> {
    this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.wait);
    // get editable featurelayer from arbeitsbereich service
    const editableFeatureLyr: __esri.FeatureLayer = this._arbeitsbereichService.arbeitsbereichEditableLyr;
    // parse objectId als integer
    const deleteObjectId: number = parseInt(
      this._arbeitsbereichService.detailFormDataModel.selectedFeature.attributes['OBJECTID']
    );
    if (isNaN(deleteObjectId)) {
      this._messageService.error('NOTIFICATIONS.DELETE_FAILED');
      throw new Error('delete object id is NaN');
    }
    // erstelle deletedFeature object
    const featureToBeDeleted = { deleteFeatures: [{ objectId: deleteObjectId }] };
    return this.delete(editableFeatureLyr, featureToBeDeleted).pipe(
      map(res => {
        this._mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
        if (res) this._arbeitsbereichService.arbeitsbereichMapLyr.refresh();
        return res;
      })
    );
  }

  /**
   * @function startEditingGeometry
   * @description start editing feature geometry based on sketchviewmodel
   */
  public startEditingGeometry(featureParams: DetailViewFeatureParams) {
    this.selectionService.removeAllSelectedGraphics();
    this._editGraphicsLayer = this._sketchGeometryService.createGraphicsLayer('edit');
    this._editingGraphic = this._sketchGeometryService.createEditingGraphic(featureParams.feature.geometry);
    this._editGraphicsLayer.add(this._editingGraphic);
    const esriGeometryType: string = featureParams.feature.geometry.type;
    this._sketchViewModel = this._sketchGeometryService.setupSketch(
      this._editGraphicsLayer,
      true,
      false,
      true,
      esriGeometryType
    );
    this._mainMapService.mapView.map.layers.add(this._editGraphicsLayer);
    const self = this;
    setTimeout(function () {
      self._sketchViewModel.update([self._editingGraphic]);
    }, 500);
  }

  /* private Methode */

  /**
   * @param featureLyr
   * @param featureToBeSaved
   * @description delete feature from service by applyedits
   */
  private save(featureLyr: __esri.FeatureLayer, featureToBeSaved: __esri.FeatureLayerApplyEditsEdits): Observable<any> {
    const response = { result: null, success: null };
    return from(
      featureLyr
        .applyEdits(featureToBeSaved)
        .then((result: any) => {
          this._messageService.success('NOTIFICATIONS.SAVE_SUCCESS');
          response.result = result;
          response.success = true;
          return response;
        })
        .catch(error => {
          this._messageService.error('NOTIFICATIONS.SAVE_FAILED');
          console.log(`update or create feature: ${error}`);
          response.success = false;
          return response;
        })
    );
  }

  /**
   * @param featureLyr
   * @param featureToBeDelete
   * @description delete feature from service by applyedits
   */
  private delete(
    featureLyr: __esri.FeatureLayer,
    featureToBeDelete: __esri.FeatureLayerApplyEditsEdits
  ): Observable<any> {
    return from(
      featureLyr
        .applyEdits(featureToBeDelete)
        .then(res => {
          this._messageService.success('NOTIFICATIONS.DELETE_SUCCESS');
          this.refreshMap();
          return res;
        })
        .catch(error => {
          this._messageService.error('NOTIFICATIONS.SAVE_FAILED');
          console.log(`delete feature: ${error}`);
          return error;
        })
    );
  }

  /**
   * @param detailFormDataModel
   * @param featureParams
   * @description return action object depending on create or update
   */
  private getWriteActionForFeature(detailFormDataModel, featureParams: DetailViewFeatureParams): any {
    if (!featureParams.createMode) {
      // feature geometry edited
      if (this._sketchGeometryService.newFeatureGeometry) {
        detailFormDataModel.selectedFeature.geometry = this._sketchGeometryService.newFeatureGeometry;
      }
      return { updateFeatures: [detailFormDataModel.selectedFeature] };
    }
    return { addFeatures: [detailFormDataModel.selectedFeature] };
  }

  /**
   * @param featureGeometryEdited
   * @param featureToBeUpdated
   * @description select the edited feature after editing its geometry
   */
  private selectEditedFeature(featureGeometryEdited: boolean, featureToBeUpdated: __esri.FeatureLayerApplyEditsEdits) {
    if (!featureGeometryEdited) return;
    this.selectionService.removeAllSelectedGraphics();
    this.selectionService.addSelectedGraphic(featureToBeUpdated.updateFeatures[0]);
  }

  private completeCreateFeature(
    detailFormDataModel: DetailFormDataModel,
    featureParams: DetailViewFeatureParams,
    newFeature
  ) {
    this._detailViewNotifyService.featureCreatedSubject.next();
    detailFormDataModel.selectedFeature.attributes.OBJECTID = newFeature.objectId;
    detailFormDataModel.selectedFeature.attributes.GLOBALID = newFeature.globalId;
    this.setStateOnCreateNewFeature(featureParams, newFeature.objectId);
    featureParams.createMode = false;
  }

  /**
   * @param detailFormDataModel
   * @param featureParams
   * @description return action object depending on create or update
   */
  private updateFeatureGeometry(detailFormDataModel: DetailFormDataModel, featureParams: DetailViewFeatureParams): any {
    if (featureParams.createMode) return;

    if (this._sketchGeometryService.newFeatureGeometry) {
      detailFormDataModel.selectedFeature.geometry = this._sketchGeometryService.newFeatureGeometry;
      this.deactivateEditTool();
      this._sketchGeometryService.newFeatureGeometry = null;
    }
  }

  /**
   * @function refreshMap
   * @description refresh services after create or delete a feature
   */
  private refreshMap() {
    const mapId = this._arbeitsbereichService.selectedArbeitsbereich.layers[0].fkLayerEdit;
    const editMap = this._layerContainerService.layerList.filter(f => f.esriLayer.id === mapId)[0];
    const editMapLayer: __esri.MapImageLayer = editMap.esriLayer as __esri.MapImageLayer;
    editMapLayer.refresh();

    const mapFeatureId = this._arbeitsbereichService.selectedArbeitsbereich.layers[0].fkTooltipLayer;
    const editFeatureMap = this._layerContainerService.layerList.filter(f => f.esriLayer.id === mapFeatureId)[0];
    const editFeatureLayer: __esri.FeatureLayer = editFeatureMap.esriLayer as __esri.FeatureLayer;
    editFeatureLayer.refresh();
  }

  private setStateOnCreateNewFeature(featureParams: DetailViewFeatureParams, newObjectId: number): void {
    const ws = this._arbeitsbereichService.selectedArbeitsbereich.id as Enums.Workspace;
    const toolCode: Enums.ToolCode = Enums.ToolCode.info;
    const options: Partial<GlobalState> = {
      workspace: ws,
      tool: toolCode,
      layer: featureParams.currentArbeitsBereichLyrs[0].esriLayer.id.toLowerCase(),
      id: newObjectId,
    };
    // set direct link
    this.urlStateHelperService.setState(options);
  }
}
